import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useStudentData } from '@/hooks/useStudentData';
import { useLevels, activeLevelNames } from '@/hooks/useLevels';

export default function StudentProfil() {
  const { profile, user } = useAuth();
  const { student, group } = useStudentData();
  const { levels } = useLevels();
  const levelOptions = activeLevelNames(levels);

  // Bilgi formu
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [level, setLevel] = useState('');
  const [savingInfo, setSavingInfo] = useState(false);
  const [infoMsg, setInfoMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Şifre formu
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name ?? '');
      setPhone(profile.phone ?? '');
    }
  }, [profile]);

  useEffect(() => {
    if (student?.level) setLevel(student.level);
  }, [student]);

  async function handleSaveInfo() {
    if (!profile) return;
    setSavingInfo(true);
    setInfoMsg(null);
    try {
      const { error: profileError } = await supabase
        .from('zehrahoca_profiles')
        .update({ full_name: fullName.trim(), phone: phone.trim() })
        .eq('id', profile.id);
      if (profileError) throw profileError;

      const { error: studentError } = await supabase
        .from('zehrahoca_students')
        .update({ level: level.trim() })
        .eq('id', profile.id);
      if (studentError) throw studentError;

      setInfoMsg({ type: 'success', text: 'Bilgileriniz başarıyla güncellendi.' });
    } catch (err) {
      setInfoMsg({ type: 'error', text: (err as Error).message || 'Bilgiler güncellenirken bir hata oluştu.' });
    } finally {
      setSavingInfo(false);
    }
  }

  async function handleChangePassword() {
    setSavingPassword(true);
    setPasswordMsg(null);
    try {
      if (newPassword.length < 6) {
        setPasswordMsg({ type: 'error', text: 'Yeni şifre en az 6 karakter olmalı.' });
        return;
      }
      if (newPassword !== confirmPassword) {
        setPasswordMsg({ type: 'error', text: 'Şifreler birbiriyle eşleşmiyor.' });
        return;
      }
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      setNewPassword('');
      setConfirmPassword('');
      setPasswordMsg({ type: 'success', text: 'Şifreniz başarıyla değiştirildi.' });
    } catch (err) {
      setPasswordMsg({ type: 'error', text: (err as Error).message || 'Şifre değiştirilirken bir hata oluştu.' });
    } finally {
      setSavingPassword(false);
    }
  }

  const firstName = (profile?.full_name ?? 'Öğrenci').split(' ')[0];
  const registerDate = student?.register_date
    ? new Date(student.register_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
    : '—';

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Profilim</h1>
        <p className="mt-1 text-sm text-foreground-600">Hesap bilgilerinizi ve şifrenizi buradan yönetin.</p>
      </div>

      {/* Genel bakış kartı */}
      <section className="overflow-hidden rounded-2xl border border-background-200 bg-gradient-to-br from-primary-50 via-background-50 to-accent-50">
        <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-primary-500 font-heading text-3xl font-extrabold text-white">
            {firstName.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-xl font-bold text-foreground-950">{profile?.full_name ?? 'Öğrenci'}</h2>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground-600">
              {group && (
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-team-line text-foreground-500" /> {group.name}
                </span>
              )}
              {student?.level && (
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-bar-chart-2-line text-foreground-500" /> {student.level}
                </span>
              )}
            </p>
          </div>
          <div className="flex shrink-0 gap-6">
            <div className="text-center">
              <p className="text-xs font-semibold text-foreground-500">Kayıt Tarihi</p>
              <p className="mt-0.5 text-sm font-semibold text-foreground-900">{registerDate}</p>
            </div>
            <div className="text-center">
              <p className="text-xs font-semibold text-foreground-500">Durum</p>
              <p className="mt-0.5 flex items-center justify-center gap-1 text-sm font-semibold text-emerald-600">
                <span className="h-2 w-2 rounded-full bg-emerald-500" /> Aktif
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bilgilerimi Düzenle */}
      <section className="rounded-2xl border border-background-200 bg-white p-6">
        <div className="mb-5 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
            <i className="ri-user-settings-line text-lg" />
          </span>
          <div>
            <h2 className="font-heading text-base font-bold text-foreground-950">Bilgilerimi Düzenle</h2>
            <p className="text-xs text-foreground-500">Adınızı, telefonunuzu ve seviyenizi güncelleyin.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="profil-ad" className="mb-1.5 block text-sm font-medium text-foreground-700">
              Ad Soyad
            </label>
            <input
              id="profil-ad"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              placeholder="Adınız ve soyadınız"
            />
          </div>
          <div>
            <label htmlFor="profil-tel" className="mb-1.5 block text-sm font-medium text-foreground-700">
              Telefon
            </label>
            <input
              id="profil-tel"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              placeholder="05xx xxx xx xx"
            />
          </div>
          <div>
            <label htmlFor="profil-seviye" className="mb-1.5 block text-sm font-medium text-foreground-700">
              Seviye
            </label>
            <select
              id="profil-seviye"
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            >
              {levelOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="profil-email" className="mb-1.5 block text-sm font-medium text-foreground-700">
              E-posta
            </label>
            <input
              id="profil-email"
              type="email"
              value={user?.email ?? profile?.email ?? ''}
              disabled
              className="w-full cursor-not-allowed rounded-lg border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-500"
            />
            <p className="mt-1 text-xs text-foreground-400">Giriş e-postanız değiştirilemez.</p>
          </div>
        </div>

        {infoMsg && (
          <div
            className={`mt-4 rounded-lg px-4 py-2.5 text-sm ${
              infoMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
            }`}
          >
            {infoMsg.text}
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={handleSaveInfo}
            disabled={savingInfo}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer"
          >
            {savingInfo ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
            {savingInfo ? 'Kaydediliyor...' : 'Bilgileri Kaydet'}
          </button>
        </div>
      </section>

      {/* Şifre Değiştir */}
      <section className="rounded-2xl border border-background-200 bg-white p-6">
        <div className="mb-5 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
            <i className="ri-lock-password-line text-lg" />
          </span>
          <div>
            <h2 className="font-heading text-base font-bold text-foreground-950">Şifre Değiştir</h2>
            <p className="text-xs text-foreground-500">Güçlü bir şifre seçmeniz hesabınızı daha güvenli kılar.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="profil-yeni-sifre" className="mb-1.5 block text-sm font-medium text-foreground-700">
              Yeni Şifre
            </label>
            <input
              id="profil-yeni-sifre"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              placeholder="En az 6 karakter"
            />
          </div>
          <div>
            <label htmlFor="profil-sifre-tekrar" className="mb-1.5 block text-sm font-medium text-foreground-700">
              Yeni Şifre (Tekrar)
            </label>
            <input
              id="profil-sifre-tekrar"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              placeholder="Şifreyi tekrar girin"
            />
          </div>
        </div>

        {passwordMsg && (
          <div
            className={`mt-4 rounded-lg px-4 py-2.5 text-sm ${
              passwordMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
            }`}
          >
            {passwordMsg.text}
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={handleChangePassword}
            disabled={savingPassword}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition hover:bg-accent-600 disabled:opacity-70 cursor-pointer"
          >
            {savingPassword ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-key-2-line" />}
            {savingPassword ? 'Değiştiriliyor...' : 'Şifreyi Güncelle'}
          </button>
        </div>
      </section>
    </div>
  );
}